import { getScale } from 'svelte-plots-basic/utils';
import { escapeText } from '../utils.js';
import { PLOT_FONT_SIZE, AXES_MARGIN_FACTORS, TICK_SIZE, LABELS_MARGIN, COLMAP_WIDTH_RATIO }
   from 'svelte-plots-basic/constants';

/**
 * Sizes that svelte-plots-basic uses for a plot of this size.
 * @param {number} width - width of the plot in pixels.
 * @param {number} height - height of the plot in pixels.
 * @returns {Object} 'fontSize' (also the tick labels' size), 'unit' (pixels per margin unit),
 * 'tick' (tick length) and 'gap' (from a tick to its label), in pixels.
 */
export function plotScale(width, height) {
   const scale = getScale(width, height);
   return { fontSize: PLOT_FONT_SIZE[scale], unit: AXES_MARGIN_FACTORS[scale],
      tick: TICK_SIZE[scale], gap: LABELS_MARGIN[scale] };
}

/**
 * Attachment that reports the size of the svg a plot draws in, as Axes reads it.
 * @param {Function} set - takes [width, height] in pixels on every change.
 * @returns {Function} the attachment, for an element holding the plot.
 */
export function plotSize(set) {
   return node => {
      const svg = node.querySelector('svg.plot');
      const ro = new ResizeObserver(() => set([svg.clientWidth, svg.clientHeight]));
      ro.observe(svg);
      return () => ro.disconnect();
   };
}

// canvas for measuring labels, made on first use
let context = null;

/**
 * Width of the widest label in the plots' font.
 * @param {Array} labels - the labels.
 * @param {number} fontSize - font size in pixels.
 * @returns {number} the width in pixels, 0 for no labels.
 */
export function textWidth(labels, fontSize) {
   context ??= document.createElement('canvas').getContext('2d');
   context.font = fontSize + 'px Arial, Helvetica, sans-serif';
   return Math.max(0, ...labels.map(l => context.measureText(l).width));
}

/**
 * Names cut to fit a given width: a name that fits, or has at most one character, stays whole;
 * else the longest start of it with an ellipsis that fits; else its first non-space character.
 * @param {Array} names - the names.
 * @param {number} room - width each name may take.
 * @param {Function} measure - width of a string.
 * @returns {Array} the names as they fit.
 */
export function fitNames(names, room, measure) {
   return names.map(name => {
      // code points, so a cut never splits a surrogate pair
      const chars = Array.from(name);
      if (chars.length <= 1 || measure(name) <= room) return name;
      for (let k = chars.length - 1; k > 0; k--) {
         const stem = chars.slice(0, k).join('').trimEnd();
         if (stem.length > 0 && measure(stem + '…') <= room) return stem + '…';
      }
      return Array.from(name.trimStart())[0] ?? name;
   });
}

/**
 * Class names of a colour bar legend cut so that each fits its cell, as Axes lays the legend
 * out: the cells share COLMAP_WIDTH_RATIO of the axes width and the labels are 0.85 em.
 * @param {Array} names - the class names, one per cell.
 * @param {Array} size - [width, height] of the plot in pixels, see 'plotSize'.
 * @param {Array} margins - the margins given to Axes.
 * @param {boolean} yAxis - the plot has a YAxis, which takes 1.5 font sizes for its label even
 * without one.
 * @returns {Array} the names as they fit.
 */
export function legendNames(names, size, margins, yAxis) {
   const sc = plotScale(...size);
   const left = yAxis ? 1.5 * sc.fontSize : 0;
   const axisWidth = size[0] - left - (margins[1] + margins[3]) * sc.unit;
   const cell = axisWidth * COLMAP_WIDTH_RATIO / names.length;
   const fs = 0.85 * sc.fontSize;
   // cut on the text as drawn, then escaped for the plot
   return fitNames(names, cell - 0.3 * fs, s => textWidth([s], fs)).map(escapeText);
}
